import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import DataFreshnessBadge from "../components/DataFreshnessBadge";
import AIDisclaimer from "../components/AIDisclaimer";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

function KPIValue({ pack }) {
  if (!pack) return <span className="text-white/30">--</span>;
  if (pack.status === "data_missing")
    return (
      <span data-testid="kpi-missing" className="text-red-300">
        Date indisponibile
      </span>
    );
  if (typeof pack.value === "number")
    return <span>{pack.value.toLocaleString("ro-RO")}</span>;
  return <span className="text-white/30">--</span>;
}

function SourceFootnote({ source }) {
  if (!source) return null;
  return (
    <p className="mt-2 text-[11px] text-white/35">
      {source.database}.{source.collection}
    </p>
  );
}

const GROUPS = [
  {
    key: "erp",
    title: "ERP (app.amel-online.ro)",
    fields: [
      ["contacts_total", "Contacte CRM"],
      ["leads_total", "Lead-uri"],
      ["offers_total", "Oferte"],
      ["sales_orders_total", "Comenzi vanzare"],
      ["sales_orders_last_30d", "Comenzi ultimele 30 zile"],
    ],
  },
  {
    key: "project",
    title: "Project B2B (project.amel.ro)",
    fields: [
      ["campaign_contacts", "Contacte campanie"],
      ["outreach_queue", "Coada outreach"],
      ["outreach_sent_last_7d", "Trimise ultimele 7 zile"],
      ["competitors_tracked", "Competitori monitorizati"],
      ["leads_marketing", "Lead-uri marketing"],
    ],
  },
  {
    key: "oferte",
    title: "Configurator (oferte.amel-online.ro)",
    fields: [
      ["culori_catalog", "Culori catalog"],
      ["game_produse", "Gama produse"],
      ["feronerie_catalog", "Feronerie catalog"],
    ],
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: resp } = await api.get("/api/datahub/snapshot");
      setData(resp);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nu am putut incarca snapshot-ul");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/api/datahub/refresh");
      await load();
      toast.success("Snapshot regenerat");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Refresh esuat");
    } finally {
      setRefreshing(false);
    }
  };

  const snap = data?.snapshot;
  const freshness = data?.freshness;
  const storedAt = freshness?.stored_at || snap?.generated_at || null;
  const ttl = freshness?.ttl_seconds || 900;

  return (
    <Layout title="Vedere de ansamblu">
      <section className="mb-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/40">Sesiune 2 - DataHub</p>
            <h2 data-testid="dashboard-greeting" className="text-2xl font-semibold">
              Buna, {user?.name || "utilizator"}.
            </h2>
            <p className="mt-1 text-sm text-white/60 max-w-2xl">
              Snapshot agregat din cele trei surse. Toate cifrele au sursa atasata
              (baza.colectie). Conexiunile catre surse sunt read-only.
            </p>
          </div>
          <DataFreshnessBadge timestamp={storedAt} ttlSeconds={ttl} onRefresh={onRefresh} />
        </div>
      </section>

      {loading ? (
        <div data-testid="dashboard-loading" className="text-white/50">Incarc...</div>
      ) : (
        <div className="space-y-8">
          {GROUPS.map((group) => (
            <section key={group.key}>
              <h3 className="mb-3 text-sm uppercase tracking-widest text-white/50">{group.title}</h3>
              <div data-testid={`group-${group.key}`} className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                {group.fields.map(([fkey, label]) => {
                  const pack = snap?.kpis?.[group.key]?.[fkey];
                  return (
                    <article
                      key={fkey}
                      data-testid={`kpi-${group.key}-${fkey}`}
                      className="rounded-lg border border-white/10 bg-ink-900 p-4"
                    >
                      <div className="text-xs uppercase tracking-widest text-white/40">{label}</div>
                      <div className="mt-3 text-2xl font-semibold">
                        <KPIValue pack={pack} />
                      </div>
                      <SourceFootnote source={pack?.source} />
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <AIDisclaimer>
        Cifrele nu sunt generate de AI. Sunt counts reali din bazele sursa. AI Board
        si recomandari automate apar in sesiunile urmatoare.
      </AIDisclaimer>

      {refreshing ? <div className="mt-3 text-xs text-white/50">Reincarc...</div> : null}
    </Layout>
  );
}
